import i18n from 'i18next'
import NumberConverter from './NumberConverter'

const localeCache: { [locale: string]: RegExp } = {}

const nonDecimalRegex = /[^\d.\-e|\-E]/g

export default class StringConverter {
  protected readonly value: string

  constructor(value: string) {
    this.value = value
  }

  public toNumber(locale: string = i18n.language): number {
    return parseFloat(this.valueAsNumericStringFor(locale))
  }

  public toNumberConverter(locale: string = i18n.language): NumberConverter {
    return new NumberConverter(this.toNumber(locale))
  }

  public isValid(locale: string = i18n.language): boolean {
    try {
      return !isNaN(this.toNumber(locale))
    } catch {
      return false
    }
  }

  // Whether this value carried a magnitude shorthand suffix (`m`/`b`).
  // Always false here; overridden by MagnitudeStringConverter.
  public hasMagnitudeSuffix(): boolean {
    return false
  }

  // first char is valid for locale but not yet a number
  public isValidStartChar(locale: string = i18n.language) {
    return this.value.match(new RegExp(`^-?0?([${this.getDecimalSymbolFor(locale)}]0*)?$`))
  }

  /**
   * Returns true only if the value contains only digits and decimal symbol
   * @param locale
   */
  public isValidStrict(locale: string = i18n.language) {
    const decimal = this.getDecimalSymbolFor(locale)
    return !!this.value.match(new RegExp(`^-?\\d*(\\${decimal}\\d*)?$`))
  }

  protected valueAsNumericStringFor(locale: string) {
    return this.value.replace(this.decimalRegexFor(locale), '.').replace(nonDecimalRegex, '')
  }

  protected decimalRegexFor(locale: string) {
    if (!localeCache[locale]) {
      localeCache[locale] = this.generateDecimalRegexFor(locale)
    }
    return localeCache[locale]
  }

  private generateDecimalRegexFor(locale: string) {
    const decimal = this.getDecimalSymbolFor(locale)
    return new RegExp(`${this.escapeAllCharacters(decimal)}`, 'g')
  }

  private getDecimalSymbolFor(locale: string = i18n.language) {
    return (
      Intl.NumberFormat(locale)
        // magic number to have output with both grouping and decimal, so the parts can be identified.
        .formatToParts(1000000.99)
        .find((p) => p.type === 'decimal')!.value
    )
  }

  private escapeAllCharacters(value: string) {
    return value
      .split('')
      .map((c) => `\\${c}`)
      .join('')
  }
}
